// web/src/lib/generationMusic.ts
// Background score for the generation flow (Tone.js). On the ignition transition the music does
// NOT hard-cut — the DRY signal FADES OUT over a few seconds while a long reverb TAIL swells and
// a lowpass slowly closes, so the track recedes into a distant reverberant wash. The signal is
// split (player → dry-gain, player → reverb) so fading the dry doesn't starve the reverb send —
// the tail rings loud from the full input even as the audible music fades. Module singleton; a
// `generation` token cancels stale async starts (StrictMode start→stop→start / load race).
import * as Tone from "tone";
import type { Mode } from "@/types/roast";

// Generation-flow score, split by mode. To REGISTER a track: drop the file in the matching folder
// under web/public/audio/music/ and add its public path to the matching list below.
//   roast  → web/public/audio/music/roast/   (plays only in roast mode)
//   toast  → web/public/audio/music/toast/   (plays only in toast mode)
//   shared → web/public/audio/music/         (plays in either mode)
// startGenerationMusic(mode) picks one at random from [mode pool + shared]; every track is looped,
// mixed at TRACK_VOLUME, and gets the same fade-out + distant reverb tail on the ignition.
const TRACKS: { roast: readonly string[]; toast: readonly string[]; shared: readonly string[] } = {
  roast: [
    // "/audio/music/roast/<file>.mp3",
  ],
  toast: [
    // "/audio/music/toast/<file>.mp3",
  ],
  shared: [
    "/audio/music/generation-theme.mp3", // plays in either mode
  ],
};
const TRACK_VOLUME = -9; // dB
const REVERB_DECAY = 23; // seconds — long cinematic reverb
const TAIL_S = 23; // reverb-tail lifetime after the dry has faded
const FADE_S = 5; // the dry music fades out over this on the transition (plays a little longer, no cut)

type MusicNodes = { p: Tone.Player; dry: Tone.Gain; rev: Tone.Reverb; revGain: Tone.Gain; filt: Tone.Filter };
let nodes: MusicNodes | null = null;
let generation = 0; // bumped on every stop/end; a start whose token is stale disposes itself

function disposeNodes(n: MusicNodes): void {
  try { n.p.dispose(); n.dry.dispose(); n.rev.dispose(); n.revGain.dispose(); n.filt.dispose(); } catch { /* already gone */ }
}

/** Start the looping score for the given mode (idempotent) — a random track from that mode's pool
 *  plus the shared pool. Needs a prior user gesture (the Create "do it" click gives the page sticky
 *  activation, so Tone.start() can resume the context here). Fails silent. */
export async function startGenerationMusic(mode: Mode): Promise<void> {
  if (nodes) return;
  const pool = [...TRACKS[mode], ...TRACKS.shared]; // mode-specific tracks + shared tracks
  if (pool.length === 0) return;
  const token = ++generation;
  try {
    await Tone.start();
    const url = pool[Math.floor(Math.random() * pool.length)]; // random track for this mode per run
    const rev = new Tone.Reverb({ decay: REVERB_DECAY, preDelay: 0.02, wet: 1 }); // fully wet → reverb-only output
    const filt = new Tone.Filter({ type: "lowpass", frequency: 20000, rolloff: -24 }); // open during playback
    const dry = new Tone.Gain(1); // dry music path — faded to 0 on the transition
    const revGain = new Tone.Gain(0.16); // reverb send level (light wash during playback, up on the tail)
    const p = new Tone.Player({ url, loop: true, volume: TRACK_VOLUME, fadeIn: 0.5 });
    p.connect(dry); // dry path: player → dry → filter
    p.connect(rev); // reverb path: player → reverb → revGain → filter  (kept full so the tail stays big)
    dry.connect(filt);
    rev.connect(revGain);
    revGain.connect(filt);
    filt.toDestination();
    await Promise.all([Tone.loaded(), rev.ready]);
    if (token !== generation) { disposeNodes({ p, dry, rev, revGain, filt }); return; } // superseded mid-load
    nodes = { p, dry, rev, revGain, filt };
    p.start();
  } catch {
    /* audio blocked / no context — skip the score silently */
  }
}

/** Ignition transition: FADE the dry music out (not a hard cut) while the reverb swells and the
 *  lowpass closes, then a long distant reverb tail rings out. */
export function endGenerationMusicWithTail(): void {
  generation++; // cancel any in-flight start
  const n = nodes;
  nodes = null;
  if (!n) return;
  try {
    n.dry.gain.rampTo(0, FADE_S, "+0.1"); // fade the audible (dry) music out over FADE_S — plays longer, no cut
    n.revGain.gain.rampTo(0.55, FADE_S * 0.6); // bring the reverb wash up as the dry fades
    n.filt.frequency.rampTo(700, 0.4, "+0.02"); // HIGH-CUT immediately at the transition → muffled while it fades
    n.filt.frequency.rampTo(300, TAIL_S, "+0.6"); // then keep receding further into the distance over the tail
    n.p.stop("+" + (FADE_S + 0.4)); // stop the source once the dry has faded; the reverb tails from the full input
    setTimeout(() => disposeNodes(n), (FADE_S + TAIL_S + 3) * 1000);
  } catch {
    disposeNodes(n);
  }
}

/** Hard stop (cancel / unmount without a transition) — no tail. */
export function stopGenerationMusic(): void {
  generation++; // cancel any in-flight start
  const n = nodes;
  nodes = null;
  if (!n) return;
  try { n.p.stop(); } catch { /* */ }
  disposeNodes(n);
}
